import type { Configuration } from '@azure/msal-browser';
import { PublicClientApplication, LogLevel } from '@azure/msal-browser';

// Validate required environment variables
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;
const redirectUri = import.meta.env.VITE_ENTRA_REDIRECT_URI;

if (!clientId || !tenantId || !redirectUri) {
  throw new Error(
    'Missing required MSAL configuration. Please check environment variables: ' +
    'VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID, VITE_ENTRA_REDIRECT_URI'
  );
}

/**
 * MSAL Configuration
 * Configuration object for Microsoft Authentication Library (MSAL)
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Use sessionStorage for security
    storeAuthStateInCookie: false, // Set to true for IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
    },
  },
};

/**
 * Scopes for API access
 * Add scopes here for Microsoft Graph API or custom API
 */
export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Scopes for silent token acquisition
 */
export const tokenRequest = {
  scopes: ['User.Read'],
};

/**
 * Create MSAL instance
 * This is the main instance used throughout the application
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL instance
 * Must be called before using the instance
 */
export const initializeMsal = async (): Promise<void> => {
  try {
    await msalInstance.initialize();
    
    // Handle redirect promise
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log('MSAL redirect handled successfully');
    }
  } catch (error) {
    console.error('MSAL initialization error:', error);
    throw error;
  }
};

/**
 * Extract user roles from ID token claims
 */
export const extractUserRoles = (account: any): string[] => {
  if (!account?.idTokenClaims) {
    return [];
  }

  const claims = account.idTokenClaims as any;
  const roles = claims.roles || [];

  return roles as string[];
};
